import { useCallback, useEffect, useState } from 'react';
import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ModellerRoutes } from './modeller/routes';
import { ModellerGate } from './modeller/shell/Gate';
import { useTokenGate } from './api/useTokenGate';

import { api, ApiError, getToken, setToken } from './api/client';
import { Fleet } from './screens/Fleet';
import { Unit } from './screens/Unit';
import { Locations } from './screens/Locations';

type Theme = 'light' | 'dark' | 'system';

function readTheme(): Theme {
  try {
    const stored = localStorage.getItem('equoia.theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* storage blocked */
  }
  return 'system';
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, set] = useState<Theme>(readTheme);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    try {
      if (theme === 'system') localStorage.removeItem('equoia.theme');
      else localStorage.setItem('equoia.theme', theme);
    } catch {
      /* storage blocked */
    }
  }, [theme]);
  return [theme, set];
}

/**
 * Token gate.
 *
 * The server offers a static staff bearer token and no user accounts, so this is
 * a text box, not a login form. Dressing it up as one would imply an identity
 * model that does not exist — there is nothing behind this token but "you work
 * here" — and would make the eventual move to real accounts look like a UI
 * change rather than the architectural one it would be.
 */
function Gate({ onAuthenticated }: { onAuthenticated: () => void }) {
  // The token logic lives in `useTokenGate`, shared with the modeller's gate.
  // Two presentations, one mechanism — a second copy would be a second way to
  // be wrong about the same credential.
  const { value, setValue, error, busy, submit } = useTokenGate(onAuthenticated);

  return (
    <div className="gate">
      <div className="panel">
        <header>Equoia fleet</header>
        <div className="body">
          <p>
            This tool uses a shared staff token, not an account. Paste the token from{' '}
            <code>EQUOIA_READ_TOKENS</code>.
          </p>
          <form onSubmit={submit} className="row">
            <input
              type="password"
              value={value}
              autoFocus
              placeholder="reader token"
              onChange={(e) => setValue(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="primary" type="submit" disabled={busy || !value.trim()}>
              {busy ? 'Checking' : 'Enter'}
            </button>
          </form>
          {error && (
            <p className="err" style={{ marginTop: 10 }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useTheme();
  const [authed, setAuthed] = useState<boolean | null>(null);
  const isModellerRoute = useLocation().pathname.startsWith('/modeller');

  const check = useCallback(async () => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    try {
      await api.fleet();
      setAuthed(true);
    } catch (err) {
      setAuthed(!(err instanceof ApiError && err.isAuth));
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  if (authed === null) return <div className="page hint">Connecting…</div>;
  // Same gate mechanism, branded for the product the caller asked for. Someone
  // opening the modeller should not be asked to sign in to the fleet tool.
  if (!authed) {
    return isModellerRoute
      ? <ModellerGate onAuthenticated={() => setAuthed(true)} />
      : <Gate onAuthenticated={() => setAuthed(true)} />;
  }

  // The modeller owns its route visually: its own 220px rail IS the
  // navigation, and rendering it under the fleet topbar gives the page two
  // chromes and two brands. The two surfaces share auth, the request client
  // and the error types — everything above this line — and share no CSS
  // identity below it.
  if (isModellerRoute) return <ModellerRoutes />;

  return (
    <>
      <header className="topbar">
        <div className="brand">
          Equoia <span>fleet</span>
        </div>
        <nav className="nav">
          <NavLink to="/fleet" className={({ isActive }) => (isActive ? 'active' : '')}>
            Fleet
          </NavLink>
          <NavLink to="/locations" className={({ isActive }) => (isActive ? 'active' : '')}>
            Locations
          </NavLink>
        </nav>
        <div className="spacer" />
        <div className="segmented" role="group" aria-label="Theme">
          {(['light', 'system', 'dark'] as const).map((t) => (
            <button key={t} aria-pressed={theme === t} onClick={() => setTheme(t)}>
              {t === 'light' ? 'Light' : t === 'dark' ? 'Dark' : 'Auto'}
            </button>
          ))}
        </div>
        <button
          className="small"
          onClick={() => {
            setToken('');
            setAuthed(false);
          }}
        >
          Sign out
        </button>
      </header>

      <Routes>
        <Route path="/" element={<Navigate to="/fleet" replace />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/units/:id" element={<Unit />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="*" element={<div className="page">Not found.</div>} />
      </Routes>
    </>
  );
}
